import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { flowerMeanings, getFlowerMeaning } from '@/data/flowerMeanings';

interface FeaturedFlowerMeaningsProps {
  language: string;
}

/**
 * 4 hand-picked flower-meaning cards rendered at the end of the city
 * home page. Two purposes:
 *
 *  1. SEO — adds 4 H3-wrapped flower names + internal links to the
 *     glossary hub and 4 detail pages on every city home (28 cities
 *     × 2 langs = 56 entry surfaces).
 *
 *  2. UX — gives the long content tail a soft commercial off-ramp.
 *     Users who hit the bottom of the page either browse products
 *     (CityIntroParagraph CTA) OR explore symbolism (this card grid).
 *
 * The 4 featured flowers are deliberately stable, not rotated:
 *   - Rose      (top commercial intent, GSC: 22K imp/mo)
 *   - Jasmine   (Egyptian cultural anchor, GSC: 248 clicks/mo)
 *   - Lotus     (Egypt\'s national flower)
 *   - Sunflower (highest single-flower CTR potential)
 *
 * Stable pick = Google can build a trust graph between city pages and
 * these glossary entries. Rotating randomly would dilute that signal.
 */
const FEATURED_SLUGS = ['rose', 'jasmine', 'lotus', 'sunflower'] as const;

const FeaturedFlowerMeanings = ({ language }: FeaturedFlowerMeaningsProps) => {
  const isAr = language === 'ar';
  const langPrefix = isAr ? 'ar-eg' : 'en-eg';
  const Arrow = isAr ? ArrowLeft : ArrowRight;

  // Pull the featured entries from the glossary in display order.
  // Fall back gracefully if any of the curated slugs disappear from
  // the data file (so the homepage never crashes on a typo).
  const featured = FEATURED_SLUGS
    .map((slug) => getFlowerMeaning(slug))
    .filter((f): f is NonNullable<ReturnType<typeof getFlowerMeaning>> => Boolean(f));

  if (featured.length === 0) return null;

  const t = isAr
    ? {
        heading: 'تعرّف على معاني الورد',
        subheading: 'كل زهرة بتقول حاجة. اقرأ معاني ورموز أشهر الورد قبل ما تختار باقتك.',
        cardCta: 'اقرأ المعنى',
        allCta: `استكشف كل الـ ${flowerMeanings.length} زهرة في القاموس`,
      }
    : {
        heading: 'Explore Flower Meanings',
        subheading: 'Every flower says something. Read the symbolism before you pick your bouquet.',
        cardCta: 'Read meaning',
        allCta: `Browse all ${flowerMeanings.length} flowers in the glossary`,
      };

  return (
    <section className="py-16 bg-gradient-to-b from-background to-muted/30" aria-labelledby="featured-flower-meanings">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className={`text-center mb-10 ${isAr ? 'text-right sm:text-center' : ''}`}>
          {/* H2 uses flex+justify-center so it stays visually centered even
              when the wrapper falls back to text-right on Arabic mobile —
              matches the NewArrivals "وصل حديثاً" header pattern exactly. */}
          <h2
            id="featured-flower-meanings"
            className="text-3xl font-bold text-foreground mb-2 flex items-center justify-center gap-2"
          >
            {t.heading}
          </h2>
          <p className="text-muted-foreground text-center">
            {t.subheading}
          </p>
        </div>

        <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((f) => {
            const name = isAr ? f.name_ar : f.name_en;
            const short = isAr ? f.short_ar : f.short_en;
            const detailUrl = `/${langPrefix}/guides/flower-meanings/${f.slug}`;
            const primarySwatch = f.colors[0]?.swatch || '#f3f4f6';

            return (
              <li key={f.slug}>
                <Link to={detailUrl} className="block group h-full">
                  <Card className="overflow-hidden h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border">
                    {/* Visual hero — color swatch with emoji */}
                    <div
                      className="aspect-square flex items-center justify-center text-6xl md:text-7xl relative"
                      style={{ backgroundColor: primarySwatch }}
                      aria-hidden="true"
                    >
                      <span className="drop-shadow-md">{f.emoji}</span>
                    </div>
                    <CardContent className="p-4 md:p-5" dir={isAr ? 'rtl' : 'ltr'}>
                      {/* H3 — counted by SEO crawlers as a heading anchor */}
                      <h3 className="font-bold text-lg md:text-xl text-foreground mb-2 group-hover:text-primary transition-colors">
                        {name}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground line-clamp-3 leading-snug mb-3">
                        {short}
                      </p>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                        {t.cardCta}
                        <Arrow className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="text-center mt-8">
          <Link
            to={`/${langPrefix}/guides/flower-meanings`}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-border bg-background hover:bg-muted rounded-md font-medium text-foreground transition-colors"
          >
            {t.allCta}
            <Arrow className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedFlowerMeanings;
